import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppService } from '../../../services/app.service';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { EnquiryRequest } from '../../../models/enquiry.model';

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss']
})
export class ProductDetailComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  productId: number;
  product: any = null;
  isLoading = true;
  selectedImage: string = '';

  // Enquiry modal
  isEnquiryModalOpen = false;
  enquiryForm: EnquiryRequest = this.getEmptyEnquiry();
  isSubmitting = false;

  constructor(
    private route: ActivatedRoute,
    private appService: AppService,
    private toaster: ToastrService
  ) {
    this.productId = Number(this.route.snapshot.paramMap.get('id'));
  }

  ngOnInit(): void {
    if (this.productId) {
      this.loadProductDetails();
    } else {
      this.isLoading = false;
    }
  }

  loadProductDetails(): void {
    this.isLoading = true;
    this.appService.getProductById(this.productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.product = data;
          this.selectedImage = this.getProductImage(this.product);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load product details', err);
          this.toaster.error('Failed to load product details');
          this.isLoading = false;
        }
      });
  }

  getProductImage(product: any): string {
    if (product?.imageDetails && product.imageDetails.length > 0) {
      const primaryImg = product.imageDetails.find((img: any) => img.primary) || product.imageDetails[0];
      return this.appService.getProductImageUrl(primaryImg.imageDetailsId);
    }
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' fill='%23ccc'%3E%3Crect width='800' height='600' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='45%25' text-anchor='middle' font-size='48' fill='%23ccc'%3E%F0%9F%93%B7%3C/text%3E%3Ctext x='50%25' y='60%25' text-anchor='middle' font-size='16' fill='%23aaa' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E`;
  }

  getThumbnailUrl(img: any): string {
    return this.appService.getProductImageUrl(img.imageDetailsId);
  }

  selectImage(img: any): void {
    this.selectedImage = this.getThumbnailUrl(img);
  }

  getSecondaryImages(product: any): any[] {
    if (product?.imageDetails && product.imageDetails.length > 1) {
      return product.imageDetails.filter((img: any) => !img.primary);
    }
    return [];
  }

  // --- Enquiry Modal ---
  getEmptyEnquiry(): EnquiryRequest {
    return {
      productId: this.productId || 0,
      customerName: '',
      phoneNumber: '',
      doorNo: '',
      village: '',
      street: '',
      city: '',
      state: '',
      pincode: '',
      message: ''
    };
  }

  openEnquiryModal(): void {
    if (!this.product) return;
    this.enquiryForm = this.getEmptyEnquiry();
    this.isEnquiryModalOpen = true;
  }

  closeEnquiryModal(): void {
    this.isEnquiryModalOpen = false;
  }

  submitEnquiry(): void {
    if (!this.enquiryForm.customerName || !this.enquiryForm.phoneNumber) {
      this.toaster.warning('Please fill in your name and phone number.');
      return;
    }
    this.isSubmitting = true;
    this.appService.sendEnquiry(this.enquiryForm).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.toaster.success('Enquiry sent successfully! We will contact you soon.');
        this.closeEnquiryModal();
      },
      error: (err) => {
        console.error('Error sending enquiry', err);
        this.isSubmitting = false;
        this.toaster.error('Failed to send enquiry. Please try again.');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
